import React, { useEffect, useState, useCallback } from "react";
import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { ticketsAPI } from "../../lib/companyAdminApi";
import { toast } from "sonner";
import {
  Ticket, Search, X, Clock, CheckCircle, AlertCircle, RefreshCw,
  MessageSquare, TrendingUp, AlertTriangle
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";

const PRIORITY_MAP = {
  high:   { label: "High",   color: "text-red-600",   bg: "bg-red-50",   border: "border-red-200",   bar: "#ef4444" },
  medium: { label: "Medium", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", bar: "#f59e0b" },
  low:    { label: "Low",    color: "text-green-600", bg: "bg-green-50", border: "border-green-200", bar: "#22c55e" },
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, colorClass, bg }) {
  return (
    <div className={"rounded-xl border p-4 flex items-center gap-4 " + (bg || "bg-card border-border")}>
      <div className={"w-11 h-11 rounded-xl flex items-center justify-center " + (colorClass || "bg-primary/10")}>
        <Icon className={"w-5 h-5 " + (colorClass ? colorClass.replace("bg-", "text-").replace("/10", "") : "text-primary")} />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground">{value ?? "—"}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-foreground">{label}</p>
      <p className="text-primary">{payload[0].value} ticket{payload[0].value !== 1 ? "s" : ""}</p>
    </div>
  );
}

// ─── Ticket Card ──────────────────────────────────────────────────────────────
function TicketCard({ ticket, onClick }) {
  const pri = PRIORITY_MAP[ticket.priority] || PRIORITY_MAP.medium;
  const isOpen = ticket.status === "open";
  return (
    <div
      data-testid={"ticket-card-" + ticket.ticket_id}
      onClick={() => onClick(ticket)}
      className="bg-card border border-border rounded-xl p-4 cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-mono text-muted-foreground shrink-0">#{ticket.ticket_id}</span>
          <span className={"shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border " + pri.bg + " " + pri.color + " " + pri.border}>
            {pri.label}
          </span>
        </div>
        <span className={"shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium " + (isOpen ? "bg-green-100 text-green-700 border border-green-200" : "bg-slate-100 text-slate-600 border border-slate-200")}>
          {isOpen ? <AlertCircle className="w-2.5 h-2.5" /> : <CheckCircle className="w-2.5 h-2.5" />}
          {isOpen ? "Open" : "Closed"}
        </span>
      </div>
      <p className="mt-2 text-sm font-semibold text-foreground line-clamp-1">{ticket.title}</p>
      {ticket.description && (
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ticket.description}</p>
      )}
      <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="font-medium text-foreground truncate">{ticket.customer_name || ticket.customer_email || "Unknown"}</span>
        <span className="shrink-0 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(ticket.created_at).toLocaleDateString()}
        </span>
        {ticket.notes?.length > 0 && (
          <span className="shrink-0 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />{ticket.notes.length}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Ticket Modal ─────────────────────────────────────────────────────────────
function TicketModal({ ticket: initial, onClose, onUpdated }) {
  const [ticket, setTicket] = useState(initial);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleClose = async () => {
    setSaving(true);
    try {
      const res = await ticketsAPI.close(ticket._id);
      setTicket(res.data); onUpdated(res.data); toast.success("Ticket closed");
    } catch { toast.error("Failed to close ticket"); }
    finally { setSaving(false); }
  };
  const handleReopen = async () => {
    setSaving(true);
    try {
      const res = await ticketsAPI.reopen(ticket._id);
      setTicket(res.data); onUpdated(res.data); toast.success("Ticket reopened");
    } catch { toast.error("Failed to reopen ticket"); }
    finally { setSaving(false); }
  };
  const handleAddNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const res = await ticketsAPI.addNote(ticket._id, { text: note });
      setTicket(res.data); setNote(""); toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
    finally { setSaving(false); }
  };

  const pri = PRIORITY_MAP[ticket.priority] || PRIORITY_MAP.medium;
  const isOpen = ticket.status === "open";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} role="presentation">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg app-modal-shell" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail-modal">
        <div className="app-modal-header flex items-start justify-between p-5 border-b border-border">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-muted-foreground">#{ticket.ticket_id}</span>
              <span className={"inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border " + pri.bg + " " + pri.color + " " + pri.border}>{pri.label}</span>
              <span className={"inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium " + (isOpen ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600")}>
                {isOpen ? <AlertCircle className="w-2.5 h-2.5" /> : <CheckCircle className="w-2.5 h-2.5" />}
                {isOpen ? "Open" : "Closed"}
              </span>
            </div>
            <p className="text-base font-semibold text-foreground mt-1">{ticket.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {ticket.customer_name || ticket.customer_email} &bull; by {ticket.created_by} &bull; {new Date(ticket.created_at).toLocaleDateString()}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="app-modal-body flex-1 overflow-y-auto min-h-0 p-5 space-y-4">
          {ticket.description && (
            <div className="bg-muted/30 rounded-lg p-3 text-sm text-foreground">{ticket.description}</div>
          )}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Notes ({ticket.notes?.length || 0})</p>
            <div className="space-y-2">
              {(ticket.notes || []).map((n, i) => (
                <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm">
                  <p className="text-foreground">{n.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.added_by} &bull; {new Date(n.added_at).toLocaleString()}</p>
                </div>
              ))}
              {(!ticket.notes || ticket.notes.length === 0) && (
                <p className="text-xs text-muted-foreground italic">No notes yet.</p>
              )}
            </div>
          </div>
          {isOpen && (
            <div className="flex gap-2">
              <input
                type="text" value={note} onChange={e => setNote(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAddNote()}
                placeholder="Add a note..."
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="ticket-note-input"
              />
              <button onClick={handleAddNote} disabled={saving || !note.trim()}
                className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-primary/90"
                data-testid="ticket-note-submit">Add</button>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted">Cancel</button>
          {isOpen ? (
            <button onClick={handleClose} disabled={saving}
              className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="close-ticket-btn">Close Ticket</button>
          ) : (
            <button onClick={handleReopen} disabled={saving}
              className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              data-testid="reopen-ticket-btn">Reopen</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function TicketsPage() {
  const [tab, setTab]           = useState("open");
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState(null);
  const [stats, setStats]       = useState(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await ticketsAPI.stats();
      setStats(res.data);
    } catch { /* non-critical */ }
    finally { setStatsLoading(false); }
  }, []);

  const fetchTickets = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await ticketsAPI.list({ status });
      setTickets(res.data.tickets || []);
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchTickets(tab); }, [tab, fetchTickets]);

  const handleUpdated = (updated) => {
    setTickets(prev => prev.map(t => t._id === updated._id ? updated : t));
    fetchStats();
  };

  const filtered = search.trim()
    ? tickets.filter(t =>
        t.title?.toLowerCase().includes(search.toLowerCase()) ||
        t.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        t.customer_email?.toLowerCase().includes(search.toLowerCase()) ||
        String(t.ticket_id).includes(search)
      )
    : tickets;

  const chartData = stats?.by_day || [];

  return (
    <CompanyAdminLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Ticket className="w-6 h-6 text-primary" /> Support Tickets
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Track and manage customer support requests</p>
          </div>
          <button
            onClick={() => { fetchStats(); fetchTickets(tab); }}
            className="p-2 rounded-lg border border-border hover:bg-muted text-muted-foreground transition-colors"
            title="Refresh" data-testid="refresh-tickets-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Tickets" value={statsLoading ? "..." : stats?.total} icon={Ticket} colorClass="bg-blue-50" />
          <StatCard label="Open" value={statsLoading ? "..." : stats?.open} icon={AlertCircle} colorClass="bg-green-50" />
          <StatCard label="Closed" value={statsLoading ? "..." : stats?.closed} icon={CheckCircle} colorClass="bg-slate-100" />
          <StatCard label="High Priority" value={statsLoading ? "..." : stats?.high_priority} icon={AlertTriangle} colorClass="bg-red-50" />
        </div>

        {/* Chart */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-primary" />
            <p className="text-sm font-semibold text-foreground">Tickets Created — Last 14 Days</p>
          </div>
          {statsLoading ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading chart...
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barSize={24}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.count > 0 ? "#6366f1" : "#e2e8f0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Priority Breakdown */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            {["high", "medium", "low"].map(p => {
              const pri = PRIORITY_MAP[p];
              const count = stats.by_priority?.[p] || 0;
              const total = stats.total || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={p} className={"rounded-xl border p-4 " + pri.bg + " " + pri.border}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={"text-xs font-semibold " + pri.color}>{pri.label} Priority</span>
                    <span className={"text-lg font-bold " + pri.color}>{count}</span>
                  </div>
                  <div className="w-full bg-card/60 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full " style={{ width: pct + "%", background: pri.bar }} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{pct}% of all tickets</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Tabs + Search */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {["open", "closed"].map(s => (
              <button
                key={s} data-testid={"tickets-tab-" + s}
                onClick={() => setTab(s)}
                className={"px-5 py-2 text-sm font-medium transition-colors capitalize " + (tab === s ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted")}
              >
                {s === "open"
                  ? <><AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />Open</>
                  : <><CheckCircle className="w-3.5 h-3.5 inline mr-1.5" />Closed</>}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tickets..."
              className="pl-8 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 w-56"
              data-testid="tickets-search"
            />
          </div>
        </div>

        {/* Ticket list */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Ticket className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">{tab === "open" ? "No open tickets" : "No closed tickets"}</p>
            <p className="text-xs mt-1">Tickets opened from customer details will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map(t => (
              <TicketCard key={t._id} ticket={t} onClick={setSelected} />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <TicketModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => { handleUpdated(updated); setSelected(updated); }}
        />
      )}
    </CompanyAdminLayout>
  );
}
